import type { LatLon } from "./common.ts";

/** Swiss LV95 (EPSG:2056) easting/northing in metres. */
export interface Lv95 {
  e: number;
  n: number;
}

/**
 * swisstopo approximate formulas (WGS84 ⇄ LV95), accurate to ~1 m inside Switzerland.
 * Good enough for placing pins on a 1–3 m lakebed grid; use GDAL/proj when cm matter.
 * Source: swisstopo "Approximate formulas for the transformation between Swiss
 * projection coordinates and WGS84".
 */
export function wgs84ToLv95({ lat, lon }: LatLon): Lv95 {
  const phi = (lat * 3600 - 169028.66) / 10000;
  const lam = (lon * 3600 - 26782.5) / 10000;
  const e =
    2600072.37 + 211455.93 * lam - 10938.51 * lam * phi - 0.36 * lam * phi * phi - 44.54 * lam ** 3;
  const n =
    1200147.07 +
    308807.95 * phi +
    3745.25 * lam * lam +
    76.63 * phi * phi -
    194.56 * lam * lam * phi +
    119.79 * phi ** 3;
  return { e, n };
}

function lv95ToWgs84Approx({ e, n }: Lv95): LatLon {
  const y = (e - 2600000) / 1e6;
  const x = (n - 1200000) / 1e6;
  const lam = 2.6779094 + 4.728982 * y + 0.791484 * y * x + 0.1306 * y * x * x - 0.0436 * y ** 3;
  const phi =
    16.9023892 +
    3.238272 * x -
    0.270978 * y * y -
    0.002528 * x * x -
    0.0447 * y * y * x -
    0.014 * x ** 3;
  return { lat: (phi * 100) / 36, lon: (lam * 100) / 36 };
}

/**
 * Inverse refined against the forward formula: ll ← ll + (approx(p) − approx(fwd(ll))).
 * The raw inverse formula is up to ~2 m off near the western border; after refinement
 * wgs84ToLv95(lv95ToWgs84(p)) ≈ p to well under 1 cm. Forward is authoritative.
 */
export function lv95ToWgs84(p: Lv95): LatLon {
  const target = lv95ToWgs84Approx(p);
  let ll = target;
  for (let i = 0; i < 3; i++) {
    const est = lv95ToWgs84Approx(wgs84ToLv95(ll));
    ll = { lat: ll.lat + (target.lat - est.lat), lon: ll.lon + (target.lon - est.lon) };
  }
  return ll;
}
